/**
 * `DELETE /contacts/:id` from the contacts manager window, behind the one
 * confirmation that names the person and what happens.
 *
 * What the server does (contacts.service.ts `deleteContact`, read 2026-10-16):
 * - 403: no `contacts.delete` grant (the route's `requireGrant`).
 * - 404 `NOT_FOUND`: already gone, OR outside an `own` scope — the server makes
 *   the two identical on purpose, so this says only that it no longer exists.
 * - 409 `CONFLICT`: named as point of contact on a legacy `d_deliverables` row
 *   (FK `ON DELETE RESTRICT`). The message reads "Contact {id} is the point of
 *   contact on {n} deliverable(s)…" — it carries the id, not the name, so the
 *   count is lifted out and the sentence is reworded around the person. No
 *   screen edits legacy deliverables, so the copy never points at one.
 * - Success: deals, quotes, invoices and credit notes that named them keep the
 *   record with no contact (`ON DELETE SET NULL`); the company's
 *   `primary_contact_id` is SET NULL too; and a company linked to Xero is
 *   re-pushed without them. The description states all three.
 *
 * ⚠️ `isPrimary` is an OPTIONAL extra over the shared contract — `Contact`
 * carries no primary flag, the company does. The host passes it so the
 * consequence sentence can say the company loses its primary contact; the
 * host must also clear its own primary id in `onDeleted`.
 *
 * Errors are held against the contact id they belong to, so a dialog mounted
 * once per window never shows one person's refusal over another person.
 */
import { useState } from 'react';
import { ConfirmDialog } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { denialReason } from '@/lib/permissions';
import type { Contact } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

export interface ContactDeleteDialogProps {
  contact: Contact | null;
  companyName: string;
  onClose: () => void;
  onDeleted: (id: number) => void;
  /** The company names this contact as its primary contact. */
  isPrimary?: boolean;
}

interface Failure {
  contactId: number;
  message: string;
  /** Pressing Delete again would get the same answer. */
  final: boolean;
  /** The server says the contact no longer exists; the host should drop it. */
  gone: boolean;
}

function deliverableCount(message: string): number | null {
  const match = /on (\d+) deliverable/.exec(message);
  return match?.[1] ? Number(match[1]) : null;
}

export function ContactDeleteDialog({
  contact,
  companyName,
  onClose,
  onDeleted,
  isPrimary = false,
}: ContactDeleteDialogProps) {
  const { ability } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const current = contact && failure?.contactId === contact.id ? failure : null;

  const close = () => {
    if (deleting) return;
    const wasGone = current?.gone ? current.contactId : null;
    setFailure(null);
    if (wasGone !== null) onDeleted(wasGone);
    else onClose();
  };

  const confirm = async () => {
    if (!contact || deleting) return;
    const target = contact;
    setDeleting(true);
    setFailure(null);
    try {
      await api.deleteContact(target.id);
      setDeleting(false);
      onDeleted(target.id);
      return;
    } catch (caught) {
      let next: Omit<Failure, 'contactId'>;
      if (caught instanceof ApiError) {
        if (caught.status === 403) {
          next = {
            message:
              denialReason(ability, 'delete', 'contacts') ??
              'You no longer have permission to delete contacts.',
            final: true,
            gone: false,
          };
        } else if (caught.status === 404) {
          next = { message: `${target.name} no longer exists.`, final: true, gone: true };
        } else if (caught.status === 409 && caught.code === 'CONFLICT') {
          const count = deliverableCount(caught.message);
          const what =
            count === null
              ? 'a deliverable'
              : `${count} ${count === 1 ? 'deliverable' : 'deliverables'}`;
          next = {
            message: `${target.name} is the point of contact on ${what}, so they can’t be deleted until they are reassigned.`,
            final: true,
            gone: false,
          };
        } else {
          next = { message: caught.formMessage ?? caught.message, final: false, gone: false };
        }
      } else {
        next = {
          message: 'Couldn’t reach the server. Check your connection.',
          final: false,
          gone: false,
        };
      }
      setFailure({ contactId: target.id, ...next });
      setDeleting(false);
    }
  };

  return (
    <ConfirmDialog
      open={contact !== null}
      onClose={close}
      onConfirm={() => void confirm()}
      title={contact ? `Delete ${contact.name}?` : 'Delete contact?'}
      description={
        contact && (
          <>
            This removes {contact.name} from {companyName}.
            {isPrimary &&
              ` They are the primary contact, so ${companyName} will have none until you choose another.`}{' '}
            Deals, quotes and invoices that name them will show no contact. If {companyName} is in
            Xero, they are removed from its Xero contact too. This can’t be undone.
          </>
        )
      }
      confirmLabel="Delete contact"
      loading={deleting}
      confirmDisabled={current?.final ?? false}
      {...(current ? { error: current.message } : {})}
    />
  );
}
